import { useEffect, useState } from "react";
import api from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";

const ymd = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const today = () => ymd(new Date());
const monthStart = () => { const d = new Date(); return ymd(new Date(d.getFullYear(), d.getMonth(), 1)); };
const fmtD = (d) => new Date(`${d}T00:00:00`).toLocaleDateString("en-IN", { day: "2-digit", month: "short", weekday: "short" });

function Stat({ label, value, testid }) {
  return (
    <div className="rounded-xl border border-teal-100 bg-white px-4 py-3" data-testid={testid}>
      <div className="text-xs uppercase tracking-wide text-slate-500">{label}</div>
      <div className="font-display text-2xl font-bold text-teal-800">{value}</div>
    </div>
  );
}

export default function DprTeamPresence({ pid }) {
  const [from, setFrom] = useState(monthStart());
  const [to, setTo] = useState(today());
  const [data, setData] = useState(null);
  useEffect(() => {
    if (!pid || !from || !to) { setData(null); return; }
    setData(null);
    api.get("/dpr/attendance-summary", { params: { project_id: pid, date_from: from, date_to: to } })
      .then((r) => setData(r.data)).catch(() => setData({ days: [], teams: [], totals: {} }));
  }, [pid, from, to]);
  const t = data?.totals || {};
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-end gap-3 rounded-lg border border-slate-200 bg-white p-3">
        <div><Label className="text-xs text-slate-500">From</Label><Input type="date" data-testid="dpr-tp-from" value={from} onChange={(e) => setFrom(e.target.value)} className="mt-1 h-9" /></div>
        <div><Label className="text-xs text-slate-500">To</Label><Input type="date" data-testid="dpr-tp-to" value={to} onChange={(e) => setTo(e.target.value)} className="mt-1 h-9" /></div>
        <Button size="sm" variant="outline" className="h-9" data-testid="dpr-tp-today" onClick={() => { setFrom(today()); setTo(today()); }}>Today</Button>
        <Button size="sm" variant="outline" className="h-9" data-testid="dpr-tp-month" onClick={() => { setFrom(monthStart()); setTo(today()); }}>This Month</Button>
      </div>
      {!pid ? <p className="text-sm text-slate-400">Select a project.</p> : !data ? <p className="text-sm text-slate-400">Loading…</p> : !data.days.length ? (
        <p className="rounded-lg border border-slate-200 bg-white px-4 py-6 text-center text-sm text-slate-400" data-testid="dpr-tp-empty">No team was marked present in this period.</p>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
            <Stat label="Total Team Present" value={t.team_days} testid="dpr-tp-total-teams" />
            <Stat label="Total Member Present" value={t.member_days} testid="dpr-tp-total-members" />
            <Stat label="Working Days" value={t.working_days} testid="dpr-tp-days" />
            <Stat label="Avg Teams / Day" value={t.avg_teams_per_day} testid="dpr-tp-avg" />
          </div>
          <div className="grid gap-3 lg:grid-cols-2">
            <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white">
              <div className="border-b bg-slate-50 px-3 py-2 text-xs font-semibold uppercase text-slate-600">Day-wise</div>
              <table className="w-full text-sm" data-testid="dpr-tp-day-table">
                <thead><tr className="border-b text-xs uppercase text-slate-500"><th className="px-3 py-2 text-left">Date</th><th className="px-3 py-2 text-right">Teams Present</th><th className="px-3 py-2 text-right">Members Present</th></tr></thead>
                <tbody>
                  {data.days.map((d) => (
                    <tr key={d.date} className="border-b last:border-0" data-testid={`dpr-tp-day-${d.date}`}>
                      <td className="px-3 py-1.5">{fmtD(d.date)}</td><td className="px-3 py-1.5 text-right font-semibold tabular-nums">{d.teams}</td><td className="px-3 py-1.5 text-right tabular-nums text-slate-600">{d.members}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot><tr className="border-t-2 bg-teal-50 font-bold text-teal-900"><td className="px-3 py-2 text-xs">TOTAL</td><td className="px-3 py-2 text-right">{t.team_days}</td><td className="px-3 py-2 text-right">{t.member_days}</td></tr></tfoot>
              </table>
            </div>
            <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white">
              <div className="border-b bg-slate-50 px-3 py-2 text-xs font-semibold uppercase text-slate-600">Team-wise ({t.distinct_teams} teams)</div>
              <table className="w-full text-sm" data-testid="dpr-tp-team-table">
                <thead><tr className="border-b text-xs uppercase text-slate-500"><th className="px-3 py-2 text-left">Team</th><th className="px-3 py-2 text-right">Days Present</th><th className="px-3 py-2 text-right">Member-Days</th></tr></thead>
                <tbody>
                  {data.teams.map((r) => (
                    <tr key={r.team_id} className="border-b last:border-0" data-testid={`dpr-tp-team-${r.team_id}`}>
                      <td className="px-3 py-1.5 font-medium text-slate-700">{r.team_name}</td><td className="px-3 py-1.5 text-right font-semibold tabular-nums">{r.days_present}</td><td className="px-3 py-1.5 text-right tabular-nums text-slate-600">{r.member_days}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
